import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import React from 'react';
import { LookBackYearRange } from '../../graphQL/types/shared';
import { ProductClass } from '../../graphQL/types/shared';
import PresentationManager from '../../presentationManager/PresentationManager';
import CountrySelector from '../countrySelector/InteriorCountrySelector';
import LeftPanelBoxShadow from '../exportBasket/LeftPanelBoxShadow';
import Feasibility from '../feasibilityChart';
import {
  Root,
} from '../Grid';
import HamburgerMenu from '../menu';
import PolicyDimensionsControl from '../policyDimensionsControl';
import SectionNav from '../sectionNav';
import SectorLabels from '../SectorLabels';
import Sidebar from '../sidebar';
import UtilityBar from '../utilityBarItems';
import {chapters} from '../utilityBarItems/VideoTutorial';
import {
  CommonElementName,
  defaultFadeTransition,
  defaultVerticalSlideTransition,
  getUniqueSlideElementKey,
  PolicyDimension,
  SlideName,
} from '../Utils';

interface Input {
  getFluentString: GetString;
  countryId: string;
  year: number;
  selectCountry: (country: string) => void;
  slideIndex: number;
  firstContentfulSlideIndex: number;
  setSlideIndex: (index: number) => void;
  goToNextSlide: () => void;
  goToPrevSlide: () => void;
  goToConclusion: () => void;
  policyDimension: PolicyDimension;
  fluentBundle: FluentBundle;
  setPolicyDimension: (value: PolicyDimension) => void;
  feasibilityTopPanelZIndex: number;
  promoteFeasibilityTopPanelZIndex: () => void;
  sectorLabelsZIndex: number;
  chartZIndex: number;
  sidebarZIndex: number;
  countrySelectorZIndex: number;
  sectionNavZIndex: number;
  hamburgerMenuZIndex: number;
  restoreCountryProfilesZIndex: () => void;
  helpTipsTriggerZIndex: number;
  toggleHelpTooltips: () => void;
  helpTooltipsActive: boolean;
}

const getGrowthOpportunitiesSlide = (input: Input) => {
  const {
    countryId, year, selectCountry,
    firstContentfulSlideIndex, slideIndex, setSlideIndex,
    goToNextSlide, goToPrevSlide, policyDimension, setPolicyDimension,
    feasibilityTopPanelZIndex, promoteFeasibilityTopPanelZIndex,
    fluentBundle, sectorLabelsZIndex,
    restoreCountryProfilesZIndex, chartZIndex, sidebarZIndex,
    countrySelectorZIndex, sectionNavZIndex, hamburgerMenuZIndex,
    getFluentString, goToConclusion, helpTipsTriggerZIndex,
    toggleHelpTooltips, helpTooltipsActive,
  } = input;

  const helpTooltipOrder = {
    dropdown: 1,
    sectionNav: 2,
    pageAdvancer: 3,
    sectorLabels: 4,
  };

  return (
    <PresentationManager RootComponent={Root} slideIndex={slideIndex}>
      <CountrySelector
        selectedCountry={countryId}
        onCountrySelect={selectCountry}
        promptText={getFluentString('country-selector-prompt')}
        getFluentString={getFluentString}
        zIndex={countrySelectorZIndex}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.CountrySelector}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.dropdown}
      />
      <SectionNav
        startingIndexOfFirstSection={0}
        slideIndex={slideIndex}
        setSlideIndex={setSlideIndex}
        year={year}
        getFluentString={getFluentString}
        countryId={countryId}
        zIndex={sectionNavZIndex}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.SectionNav}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.sectionNav}
      />
      <Sidebar
        firstContentfulSlideIndex={firstContentfulSlideIndex}
        slideIndex={slideIndex}
        getFluentString={getFluentString}
        goToNextSlide={goToNextSlide}
        goToPrevSlide={goToPrevSlide}
        countryId={countryId}
        fluentBundle={fluentBundle}
        zIndex={sidebarZIndex}
        yearRange={LookBackYearRange.FifteenYears}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.Sidebar}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.pageAdvancer}
      />
      <LeftPanelBoxShadow
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.LeftPanelBackground}
      />
      <Feasibility
        year={year}
        countryId={countryId}
        policyDimension={policyDimension}
        zIndexFromAbove={chartZIndex}
        goToConclusion={goToConclusion}
        getFluentString={getFluentString}
        transition={defaultFadeTransition}
        productClass={ProductClass.SITC}
        key={getUniqueSlideElementKey(SlideName.GrowthOpportunities, 'feasibility')}
      />
      <PolicyDimensionsControl
        policyDimension={policyDimension}
        setPolicyDimension={setPolicyDimension}
        getFluentString={getFluentString}
        countryId={countryId}
        feasibilityTopPanelZIndex={feasibilityTopPanelZIndex}
        promoteZIndex={promoteFeasibilityTopPanelZIndex}
        restoreZIndex={restoreCountryProfilesZIndex}
        fluentBundle={fluentBundle}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.GrowthOpportunities, 'policyDimensionsController')}
      />
      <HamburgerMenu
        getFluentString={getFluentString}
        zIndex={hamburgerMenuZIndex}
        transition={defaultFadeTransition}
        key={CommonElementName.HamburgerMenu}
      />
      <UtilityBar
        getFluentString={getFluentString}
        zIndex={helpTipsTriggerZIndex}
        toggleHelpTooltips={toggleHelpTooltips}
        helpTooltipsActive={helpTooltipsActive}
        transition={defaultFadeTransition}
        countryId={countryId}
        videoChapter={chapters[9]}
        key={CommonElementName.HelpTipsTrigger}
      />
      <SectorLabels
        isServicesEnabled={false}
        zIndex={sectorLabelsZIndex}
        countryId={countryId}
        hideOnTechFrontier={true}
        transition={defaultFadeTransition}
        key={CommonElementName.SectorLabels}
        getFluentString={getFluentString}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.sectorLabels}
      />
    </PresentationManager>
  );
};

export default getGrowthOpportunitiesSlide;
